import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Textarea } from '../ui/textarea';
import { PhoneOff, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  BlockedPhoneNumberRecord,
  CreateBlockedPhoneNumberPayload,
  apiService,
} from '../../services/api';
import { extractErrorMessage } from '../../utils/errorHandler';

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('de-DE');
};

export function BlockedPhoneNumbersPage() {
  const [entries, setEntries] = useState<BlockedPhoneNumberRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<BlockedPhoneNumberRecord | null>(null);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState<CreateBlockedPhoneNumberPayload>({
    phoneNumber: '',
    reason: '',
  });

  const loadEntries = async (): Promise<void> => {
    setLoading(true);
    try {
      const list = await apiService.getBlockedPhoneNumbers();
      setEntries(list);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Sperrliste konnte nicht geladen werden.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadEntries();
  }, []);

  const resetForm = (): void => {
    setForm({ phoneNumber: '', reason: '' });
  };

  const handleCreate = async (e: React.SyntheticEvent): Promise<void> => {
    e.preventDefault();
    const phone = form.phoneNumber.trim();
    if (!phone) {
      toast.error('Bitte eine gültige Telefonnummer im internationalen Format eingeben (z.B. +499871671219).');
      return;
    }
    setCreating(true);
    try {
      const created = await apiService.createBlockedPhoneNumber({
        phoneNumber: phone,
        reason: form.reason?.trim() ? form.reason.trim() : undefined,
      });
      setEntries((prev) => [created, ...prev]);
      toast.success(`${created.phoneNumber} wurde geblockt.`);
      setCreateOpen(false);
      resetForm();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Blockieren fehlgeschlagen.'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiService.deleteBlockedPhoneNumber(deleteTarget.id);
      setEntries((prev) => prev.filter((e) => e.id !== deleteTarget.id));
      toast.success(`${deleteTarget.phoneNumber} wurde aus der Sperrliste entfernt.`);
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Entfernen fehlgeschlagen.'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 flex items-center gap-2">
            <PhoneOff className="w-6 h-6 text-rose-600" />
            Gesperrte Telefonnummern
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Nummern, die niemals vom Bot angerufen werden sollen — z.B. nach User-Beschwerden oder DSGVO-Opt-Outs.
            Nur Super-Admins haben Zugriff auf diese Seite.
          </p>
        </div>
        <Button onClick={() => { setCreateOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nummer sperren
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Sperrliste</CardTitle>
          <CardDescription>
            Wird vor allen Spam-Bremsen geprüft. Block greift unmittelbar nach dem Speichern — kein Deploy nötig.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-sm text-gray-500">Lade Sperrliste…</div>
          ) : entries.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-500">
              Keine gesperrten Nummern. Klicke „Nummer sperren", um eine hinzuzufügen.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Telefonnummer</TableHead>
                  <TableHead>Grund</TableHead>
                  <TableHead>Gesperrt am</TableHead>
                  <TableHead className="text-right">Aktion</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-mono">{entry.phoneNumber}</TableCell>
                    <TableCell className="text-gray-600">{entry.reason ?? '—'}</TableCell>
                    <TableCell className="text-gray-600">{formatDate(entry.createdAt)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setDeleteTarget(entry); }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        aria-label={`${entry.phoneNumber} entsperren`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent>
          <form onSubmit={(e) => { void handleCreate(e); }}>
            <DialogHeader>
              <DialogTitle>Telefonnummer sperren</DialogTitle>
              <DialogDescription>
                Die Nummer wird sofort gesperrt — beim nächsten Anruf-Versuch des Landingpage-Bots antwortet der Server mit HTTP 403.
                Format: international mit führendem „+" (Whitespace und Trennzeichen werden automatisch entfernt).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="blocked-phone">Telefonnummer</Label>
                <Input
                  id="blocked-phone"
                  type="tel"
                  placeholder="+499871671219"
                  value={form.phoneNumber}
                  onChange={(e) => { setForm((f) => ({ ...f, phoneNumber: e.target.value })); }}
                  required
                  autoFocus
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="blocked-reason">Grund (optional)</Label>
                <Textarea
                  id="blocked-reason"
                  placeholder="z.B. User-Beschwerde 2026-05-27 — Bot ruft ständig an"
                  value={form.reason ?? ''}
                  onChange={(e) => { setForm((f) => ({ ...f, reason: e.target.value })); }}
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-gray-500">
                  Nur intern sichtbar. Wird nicht an die Landingpage zurückgespielt.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => { setCreateOpen(false); }}>
                Abbrechen
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Wird gesperrt…' : 'Nummer sperren'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Sperre aufheben?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <>
                  <strong className="font-mono">{deleteTarget.phoneNumber}</strong> kann sofort wieder vom Bot angerufen werden.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={deleting}
              className="bg-destructive text-white hover:bg-destructive/90"
            >
              {deleting ? 'Wird entfernt…' : 'Sperre aufheben'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
